/**
 * Outbox offline (ADR-0007).
 *
 * Si la ventanilla pierde la red, la mutación se encola en IndexedDB con su
 * `Idempotency-Key` y se drena al reconectar. **El servidor siempre es la autoridad**:
 * el outbox nunca duplica porque el servidor deduplica por clave.
 */
import { api, newIdempotencyKey, OfflineError, ApiProblem } from './api.js';
import { db, type OutboxEntry } from './db.js';

export interface EnqueueInput {
  path: string;
  method: OutboxEntry['method'];
  body: unknown;
  label: string;
}

const MAX_ATTEMPTS = 8;
const BASE_BACKOFF_MS = 1_500;

export async function enqueue(input: EnqueueInput): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    id: newIdempotencyKey(),
    path: input.path,
    method: input.method,
    body: input.body,
    status: 'pending',
    attempts: 0,
    createdAt: Date.now(),
    nextAttemptAt: Date.now(),
    label: input.label,
  };
  await db.outbox.put(entry);
  return entry;
}

export async function listOutbox(): Promise<OutboxEntry[]> {
  const all = await db.outbox.orderBy('createdAt').reverse().toArray();
  return all.filter((e) => e.status !== 'done');
}

export async function pendingCount(): Promise<number> {
  return db.outbox.where('status').anyOf('pending', 'sending', 'failed').count();
}

export interface DrainResult {
  sent: number;
  failed: number;
  stillPending: number;
  results: { id: string; ok: boolean; error?: string }[];
}

let draining = false;

/**
 * Intenta enviar todo lo pendiente. Es idempotente y seguro de llamar varias veces.
 * @param onSuccess se llama con el resultado de cada mutación confirmada.
 */
export async function drainOutbox(
  onSuccess?: (entry: OutboxEntry, result: unknown) => void,
): Promise<DrainResult> {
  if (draining) return { sent: 0, failed: 0, stillPending: await pendingCount(), results: [] };
  draining = true;
  const results: DrainResult['results'] = [];
  let sent = 0;
  let failed = 0;

  try {
    const now = Date.now();
    const entries = (await db.outbox.toArray())
      .filter((e) => (e.status === 'pending' || e.status === 'failed') && e.nextAttemptAt <= now)
      .sort((a, b) => a.createdAt - b.createdAt);

    for (const entry of entries) {
      await db.outbox.update(entry.id, { status: 'sending' });
      try {
        const result = await api[entry.method === 'POST' ? 'post' : entry.method === 'PATCH' ? 'patch' : 'put'](
          entry.path,
          entry.body,
          { idempotencyKey: entry.id },
        );
        await db.outbox.update(entry.id, { status: 'done' });
        sent++;
        results.push({ id: entry.id, ok: true });
        onSuccess?.(entry, result);
      } catch (error) {
        if (error instanceof ApiProblem && error.status < 500) {
          // Error de negocio: reintentar no sirve. Se marca fallido y se avisa.
          await db.outbox.update(entry.id, {
            status: 'failed',
            attempts: entry.attempts + 1,
            lastError: error.problem.detail,
            nextAttemptAt: Number.MAX_SAFE_INTEGER,
          });
          failed++;
          results.push({ id: entry.id, ok: false, error: error.problem.detail });
        } else {
          const attempts = entry.attempts + 1;
          const backoff = Math.min(BASE_BACKOFF_MS * 2 ** attempts, 5 * 60_000);
          await db.outbox.update(entry.id, {
            status: 'pending',
            attempts,
            lastError: error instanceof OfflineError ? 'sin conexión' : String(error),
            nextAttemptAt: Date.now() + backoff,
          });
          results.push({ id: entry.id, ok: false, error: 'reintentará' });
        }
      }
    }
  } finally {
    draining = false;
  }

  return { sent, failed, stillPending: await pendingCount(), results };
}

/** Limpia los envíos confirmados (se llama al cerrar sesión y al limpiar caché). */
export async function pruneOutbox(): Promise<void> {
  const done = await db.outbox.where('status').equals('done').toArray();
  await db.outbox.bulkDelete(done.map((e) => e.id));
}

export { MAX_ATTEMPTS };
